// ============================================
// Shared API Endpoints (ES6)
// Cloud Run 마이그레이션 완료 (2026-01-09)
// - 모든 유저 API: Cloud Run
// - Admin API만 Firebase Functions
// ============================================

import { FUNCTIONS_BASE, CLOUD_RUN_BASE, CLOUD_RUN_DIRECT } from '../config.js';

// ============================================
// Base URL Helper
// ============================================

function endpoint(path) {
  return `${FUNCTIONS_BASE}/${path}`;
}

// Cloud Run 엔드포인트 (Firebase Hosting 프록시 경유)
function cloudRunEndpoint(path) {
  return `${CLOUD_RUN_BASE}/${path}`;
}

// Cloud Run 직접 호출 (SSE 스트리밍용 - Firebase Hosting 버퍼링 우회)
function cloudRunStreamEndpoint(path) {
  return `${CLOUD_RUN_DIRECT}/${path}`;
}

// ============================================
// Creator Endpoints
// ============================================

export const CREATOR_ENDPOINTS = {
  // Cloud Run
  autoFillCreation: cloudRunEndpoint('autofill/creation'),
  refinePrompt: cloudRunEndpoint('autofill/refine'),
  generateDescription: cloudRunEndpoint('autofill/description'),
  saveCreation: cloudRunEndpoint('creator/save'),
  deleteCreation: cloudRunEndpoint('creator/delete'),
  updateCreationStatus: cloudRunEndpoint('creator/status'),
  generateCreatorImage: cloudRunEndpoint('image/generate'),
  transformImage: cloudRunEndpoint('image/transform'),
  loadCreationStats: cloudRunEndpoint('stats/load')
};

// ============================================
// Chat Endpoints
// ============================================

export const CHAT_ENDPOINTS = {
  // Cloud Run 직접 호출 (SSE 스트리밍)
  infiniteStreamChat: cloudRunStreamEndpoint('chat/stream'),
  // Cloud Run (Firebase Hosting 프록시)
  startPublishedChat: cloudRunEndpoint('chat/start'),
  deleteChat: cloudRunEndpoint('chat/delete'),
  deleteSession: cloudRunEndpoint('chat/delete'),    // 세션 삭제 (별칭)
  duplicateSession: cloudRunEndpoint('chat/session/duplicate'),  // 세션 복제
  updateChatSettings: cloudRunEndpoint('chat/settings'),
  toggleChatHidden: cloudRunEndpoint('chat/hidden'),
  saveUserNote: cloudRunEndpoint('chat/note'),
  updateMessage: cloudRunEndpoint('chat/message'),
  rollbackMessages: cloudRunEndpoint('chat/rollback'),
  loadChatProfile: cloudRunEndpoint('chat/profile'),
  generateSceneImage: cloudRunEndpoint('image/scene'),
  renameSession: cloudRunEndpoint('chat/session/rename')
};

// ============================================
// User Endpoints
// ============================================

export const USER_ENDPOINTS = {
  // Cloud Run
  loadUserData: cloudRunEndpoint('user/data'),
  uploadUserAvatar: cloudRunEndpoint('user/avatar'),
  updateNickname: cloudRunEndpoint('user/nickname'),
  checkAdmin: cloudRunEndpoint('user/check-admin')
};

// ============================================
// Home & Feed Endpoints
// ============================================

export const HOME_ENDPOINTS = {
  // Cloud Run
  recordStats: cloudRunEndpoint('stats/record'),
  recordStatsBatch: cloudRunEndpoint('stats/batch'),
  toggleLike: cloudRunEndpoint('social/like'),
  // 태그 API
  popularTags: cloudRunEndpoint('tags/popular'),
  searchTags: cloudRunEndpoint('tags/search'),
  trendingTags: cloudRunEndpoint('tags/trending'),
  tagDetail: cloudRunEndpoint('tags'),  // + /:tagName
  tagContents: cloudRunEndpoint('tags'),  // + /:tagName/contents
  // 유사 콘텐츠 API
  similarContent: cloudRunEndpoint('home/similar')  // + /:contentId
};

// ============================================
// Memory Endpoints (메모리 편집 - 쓰기만)
// ============================================

export const MEMORY_ENDPOINTS = {
  // World
  updateWorld: cloudRunEndpoint('memory/world/update'),
  // Relationship
  updateRelationship: cloudRunEndpoint('memory/relationship/update'),
  // Summary
  updateSummary: cloudRunEndpoint('memory/summary/update'),
  // Entity
  createEntity: cloudRunEndpoint('memory/entity'),
  updateEntity: cloudRunEndpoint('memory/entity/update'),
  deleteEntity: cloudRunEndpoint('memory/entity/delete'),
  // Event
  createEvent: cloudRunEndpoint('memory/event'),
  updateEvent: cloudRunEndpoint('memory/event/update'),
  deleteEvent: cloudRunEndpoint('memory/event/delete'),
  // Quote
  createQuote: cloudRunEndpoint('memory/quote'),
  updateQuote: cloudRunEndpoint('memory/quote/update'),
  deleteQuote: cloudRunEndpoint('memory/quote/delete')
};

// ============================================
// Payment Endpoints
// ============================================

export const PAYMENT_ENDPOINTS = {
  // Cloud Run
  getProducts: cloudRunEndpoint('payment/products'),
  verifyGoogle: cloudRunEndpoint('payment/verify-google'),
  verifyApple: cloudRunEndpoint('payment/verify-apple')
};

// ============================================
// TTS Endpoints (Google Cloud TTS - Chirp 3: HD)
// ============================================

export const TTS_ENDPOINTS = {
  // Cloud Run
  synthesize: cloudRunEndpoint('tts/synthesize'),
  voices: cloudRunEndpoint('tts/voices'),
  emotions: cloudRunEndpoint('tts/emotions')
};

// ============================================
// Profile Endpoints
// ============================================

export const PROFILE_ENDPOINTS = {
  // Cloud Run
  deletePublished: cloudRunEndpoint('creator/publish/delete'),
  checkPublishStatus: cloudRunEndpoint('creator/publish/status'),
  saveProfile: cloudRunEndpoint('profile/save'),
  deleteProfile: cloudRunEndpoint('profile/delete'),
  selectProfile: cloudRunEndpoint('profile/select'),
  setPrimaryProfile: cloudRunEndpoint('profile/primary')
};

// ============================================
// Settings & Config Endpoints (Admin)
// ============================================

export const CONFIG_ENDPOINTS = {
  loadModelConfigs: endpoint('loadModelConfigs'),
  loadImageModels: endpoint('loadImageModels')
};

// ============================================
// Helper Functions
// ============================================

export function getBaseUrl() {
  return FUNCTIONS_BASE;
}
